import { type AppConfig, isApiError } from '@fuze/portal-core';
import type { Breadcrumb, CaptureContext, ErrorEvent } from '@sentry/react';

type ReportContext = {
  /** React's component stack from an error boundary. */
  componentStack?: string | null | undefined;
  /** Where the error surfaced: 'screen' (error boundary), 'router' (loader/route), 'query' (TanStack cache). */
  source?: 'screen' | 'router' | 'query';
};

export type ErrorReporter = {
  /** Report an error and return a reference id the user can quote to support, or undefined when not reporting. */
  report: (error: unknown, context?: ReportContext) => string | undefined;
};

type SentrySdk = typeof import('./sentrySdk');

/** Browser-side network failures the fetcher already surfaces as ApiError or a retry; reporting them is noise. */
const NETWORK_NOISE = [
  /^AbortError/,
  /Failed to fetch/,
  /Load failed/,
  /NetworkError when attempting to fetch resource/,
];

const stripQuery = (url: string): string => url.split('?')[0] ?? url;

/** Query strings can carry identifiers (search terms, nuqs state); never send them. */
export const scrubEvent = <T extends ErrorEvent>(event: T): T => {
  if (event.request?.url !== undefined) {
    event.request.url = stripQuery(event.request.url);
  }
  return event;
};

export const scrubBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  const data = breadcrumb.data;
  const url: unknown = data?.url;
  if (data !== undefined && typeof url === 'string') {
    data.url = stripQuery(url);
  }
  return breadcrumb;
};

const captureContext = (error: unknown, context: ReportContext | undefined): CaptureContext => {
  const tags: Record<string, string> = {};
  if (context?.source !== undefined) {
    tags['error.source'] = context.source;
  }
  if (isApiError(error)) {
    tags['api.status'] = String(error.status);
    tags['api.code'] = error.code;
    if (error.requestId !== undefined) {
      tags['request.id'] = error.requestId;
    }
  }
  const componentStack = context?.componentStack;
  return {
    tags,
    ...(typeof componentStack === 'string' ? { contexts: { react: { componentStack } } } : {}),
  };
};

const initSentry = (sdk: SentrySdk, config: AppConfig, dsn: string): void => {
  sdk.init({
    dsn,
    environment: config.environment,
    release: __APP_RELEASE__,
    // No user info, cookies, headers, bodies or query strings leave the browser; tracing is off (tracesSampleRate
    // undefined). Enabling any of these is a Security review: a REMS portal's URLs and requests carry PHI.
    dataCollection: { userInfo: false, cookies: false, httpHeaders: false, httpBodies: [], urlQueryParams: false },
    enhanceFetchErrorMessages: 'report-only',
    ignoreErrors: NETWORK_NOISE,
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  });
};

/**
 * Sentry when a DSN is configured, otherwise the console. Errors only: no tracing, no replay, no PII.
 *
 * The SDK is loaded after the shell (dynamic import of sentrySdk.ts) so it never sits in the initial bundle; anything reported
 * before it arrives is queued and sent once it has initialized, without a reference id. Sentry's own global
 * handlers take over from that point.
 */
export const initReporting = (config: AppConfig): ErrorReporter => {
  const dsn = config.sentryDsn;
  if (dsn === undefined) {
    return {
      report: (error) => {
        console.error(error);
        return undefined;
      },
    };
  }

  let sentry: SentrySdk | undefined;
  const queued: { error: unknown; context: ReportContext | undefined }[] = [];

  void import('./sentrySdk').then((sdk) => {
    initSentry(sdk, config, dsn);
    sentry = sdk;
    for (const { error, context } of queued.splice(0)) {
      sdk.captureException(error, captureContext(error, context));
    }
  });

  return {
    report: (error, context) => {
      if (sentry === undefined) {
        queued.push({ error, context });
        return undefined;
      }
      return sentry.captureException(error, captureContext(error, context));
    },
  };
};
